import React, { useState, useEffect } from 'react';
import Glitch from 'glitch-javascript-sdk';
import Navigate from '../../../../util/Navigate';
import { useNavigate } from 'react-router-dom';
import GameTitleForm from '../../component/section/titles/title_form';
import Loading from '../../component/alerts/Loading';
import timeouts from '../../../../constants/timeouts';
import Danger from '../../component/alerts/Danger';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRobot, faEdit, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { faSteam, faWindows, faApple, faAndroid, faPlaystation, faXbox } from '@fortawesome/free-brands-svg-icons';

const PublisherOnboardingStep4Page = () => {
    const [title, setTitle] = useState({ is_media_kit: false });
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [community, setCommunity] = useState({});
    const [gameMainImageBlob, setMainImageBlob] = useState(null);
    const [gameBannerImageBlob, setBannerImageBlob] = useState(null);
    const [loadingMessage, setLoadingMessage] = useState('Searching for game data...');
    const [loadError, setLoadError] = useState(false);
    const navigate = useNavigate();
    const [externalGameTitle, setExternalGameTitle] = useState(null);
    const [communities, setCommunities] = useState([]);
    const [mediaIds, setMediaIds] = useState({
        pendingMediaIds: [],
        removedMediaIds: [],
    });

    // AI workflow state
    const [showAIWorkflow, setShowAIWorkflow] = useState(false);
    const [games, setGames] = useState([]);
    const [selectedGameId, setSelectedGameId] = useState(null);
    const [errorGameId, setErrorGameId] = useState(null);

    useEffect(() => {
        Glitch.api.Communities.list({ 
            roles: [Glitch.constants.Roles.ADMINISTRATOR, Glitch.constants.Roles.SUPER_ADMINISTRATOR, Glitch.constants.Roles.MODERATOR], 
            order_by: 'name' 
        }).then(response => {
            setCommunities(response.data.data);
            if (response.data.data && response.data.data.length === 1) {
                setTitle(prev => ({ ...prev, community_id: response.data.data[0].id }));
            }
        }).catch(error => {
            console.error(error);
        });
    }, []);

    const handleMediaIdsUpdate = ({ pendingMediaIds, removedMediaIds }) => {
        setMediaIds({ pendingMediaIds, removedMediaIds });
    };

    const handleSubmit = async () => {
        setIsLoading(true);

        try {
            const titleData = { ...title, is_media_kit: false };

            if (!title.id) {
                const response = await Glitch.api.Titles.create(titleData);
                const createdTitle = response.data.data;

                const uploadPromises = [];

                if (gameMainImageBlob) {
                    uploadPromises.push(
                        Glitch.api.Titles.uploadMainImageBlob(createdTitle.id, gameMainImageBlob)
                    );
                }

                if (gameBannerImageBlob) {
                    uploadPromises.push(
                        Glitch.api.Titles.uploadBannerImageBlob(createdTitle.id, gameBannerImageBlob)
                    );
                }

                if (mediaIds.pendingMediaIds.length > 0) {
                    mediaIds.pendingMediaIds.forEach((mediaId, idx) => {
                        uploadPromises.push(
                            Glitch.api.Titles.addMedia(createdTitle.id, { media_id: mediaId, order: idx })
                        );
                    });
                }

                if (mediaIds.removedMediaIds.length > 0) {
                    mediaIds.removedMediaIds.forEach((mediaId) => {
                        uploadPromises.push(
                            Glitch.api.Titles.removeMedia(createdTitle.id, mediaId)
                        );
                    });
                }

                if (uploadPromises.length > 0) {
                    try {
                        await Promise.all(uploadPromises);
                    } catch (error) {
                        console.error(error);
                    }
                }

                navigate(Navigate.publishersOnboardingStep5Page(createdTitle.id));
            } else {
                await Glitch.api.Titles.update(title.id, titleData);

                const uploadPromises = [];

                if (gameMainImageBlob) {
                    uploadPromises.push(
                        Glitch.api.Titles.uploadMainImageBlob(title.id, gameMainImageBlob)
                    );
                }

                if (gameBannerImageBlob) {
                    uploadPromises.push(
                        Glitch.api.Titles.uploadBannerImageBlob(title.id, gameBannerImageBlob)
                    );
                }

                if (mediaIds.pendingMediaIds.length > 0) {
                    mediaIds.pendingMediaIds.forEach((mediaId, idx) => {
                        uploadPromises.push(
                            Glitch.api.Titles.addMedia(title.id, { media_id: mediaId, order: idx })
                        );
                    });
                }

                if (mediaIds.removedMediaIds.length > 0) {
                    mediaIds.removedMediaIds.forEach((mediaId) => {
                        uploadPromises.push(
                            Glitch.api.Titles.removeMedia(title.id, mediaId)
                        );
                    });
                }

                if (uploadPromises.length > 0) {
                    await Promise.all(uploadPromises);
                }

                navigate(Navigate.publishersOnboardingStep5Page());
            }
        } catch (error) {
            const jsonErrors = error?.response?.data;
            if (jsonErrors) {
                setErrors(jsonErrors);
                setTimeout(() => {
                    setErrors({});
                }, timeouts.error_message_timeout);
            }
        } finally {
            setIsLoading(false);
        }
    };

    // AI workflow functions
    const startAIWorkflow = () => {
        setShowAIWorkflow(true);
        setTitle({ is_media_kit: false }); // Reset form when starting AI workflow
    };

    const cancelAIWorkflow = () => {
        setShowAIWorkflow(false);
        setSelectedGameId(null);
        setLoadingMessage('Searching for game data...');
        setLoadError(false);
    };

    const platformIcons = {
        Android: faAndroid,
        iOS: faApple,
        Steam: faSteam,
        Windows: faWindows,
        PlayStation: faPlaystation,
        Xbox: faXbox,
    };

    const renderPlatformIcons = (platforms) => {
        return (platforms || []).map((p, i) => (
            platformIcons[p] ? (
                <FontAwesomeIcon
                    key={`${p}-${i}`}
                    icon={platformIcons[p]}
                    className="mx-1"
                />
            ) : null
        ));
    };

    const generateFromAI = async (game_id) => {
        setIsLoading(true);
        setSelectedGameId(game_id);
        setLoadError(false);
        
        // Animate loading messages
        const loadingMessages = [
            'Searching for game data...',
            'Data found, parsing...',
            'Understanding game info...',
            'Generating optimized title...',
            'Downloading media assets...',
            'Processing data...',
            'Finalizing...',
        ];
        
        let counter = 0;
        const interval = setInterval(() => {
            setLoadingMessage(loadingMessages[counter % loadingMessages.length]);
            counter++;
        }, 2000);

        try {
            const resp = await Glitch.api.Games.createGameTitle(game_id);
            const titleData = resp.data.data.title;
            const externalGame = resp.data.data.game;

            if(communities && communities[0]) {
                titleData.community_id = communities[0].id
            }

            setTitle(titleData);
            setExternalGameTitle(externalGame);
            setShowAIWorkflow(false);
            
            // Show success message briefly
            setLoadingMessage('Success! Data loaded from store.');
            setTimeout(() => setLoadingMessage(''), 2000);
        } catch (err) {
            console.error(err);
            setLoadError(true);
            setErrorGameId(game_id);
        } finally {
            clearInterval(interval);
            setIsLoading(false);
            setSelectedGameId(null);
        }
    };

    return (
        <div className="container-fluid px-3 py-4">
            {/* Hero Section */}
            <div className="text-center mb-5">
                <div className="badge bg-light text-dark mb-3 p-2">Step 4 of 4</div>
                <h1 className="display-5 fw-bold mb-3">Tell us About Your Game! 🎮</h1>
                <p className="lead mx-auto" style={{ maxWidth: '700px' }}>
                    Now tell us some basic information about your game so you can begin marketing it. You can fill it out manually or use our AI to pull data from game stores.
                </p>
            </div>

            {/* AI Workflow Section */}
            {showAIWorkflow ? (
                <div className="card border-0 shadow-sm mb-5">
                    <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
                        <h4 className="mb-0">
                            <FontAwesomeIcon icon={faRobot} className="me-2" />
                            AI-Assisted Game Setup
                        </h4>
                        <button 
                            className="btn btn-sm btn-outline-light" 
                            onClick={cancelAIWorkflow}
                        >
                            <FontAwesomeIcon icon={faArrowLeft} className="me-1" />
                            Back to Manual Form
                        </button>
                    </div>
                    <div className="card-body">
                        <div className="text-center mb-4">
                            <h4 className='text-black'>Find Your Game</h4>
                            <p className="text-muted">
                                We'll pull your game data from sources like Steam, IGDB and others. Search across multiple stores to automatically populate your game details.
                            </p>
                        </div>

                        <div className="input-group mb-4">
                            <span className="input-group-text">
                                <FontAwesomeIcon icon={faRobot} />
                            </span>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Search for your game..."
                                onChange={(e) => {
                                    const q = e.target.value;
                                    if (q.length > 2) {
                                        Glitch.api.Games.listGames({ search: q })
                                            .then((resp) => setGames(resp.data.data))
                                            .catch(console.error);
                                    }
                                }}
                            />
                        </div>

                        {selectedGameId && isLoading ? (
                            <div className="text-center py-4">
                                <Loading />
                                <p className="mt-2">{loadingMessage}</p>
                            </div>
                        ) : (
                            <div className="list-group" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                {games.length > 0 ? (
                                    games.map((g) => {
                                        const platforms = g.store === 'steam' ? ['Steam'] : JSON.parse(g.platforms);
                                        return (
                                            <div
                                                key={g.id}
                                                className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                                            >
                                                <div className="d-flex align-items-center">
                                                    {g.image && (
                                                        <img 
                                                            src={g.image} 
                                                            alt={g.name} 
                                                            className="rounded me-3"
                                                            style={{ width: '60px', height: '45px', objectFit: 'cover' }}
                                                        />
                                                    )}
                                                    <div>
                                                        <div className="fw-bold">{g.name}</div>
                                                        <small className="text-muted">
                                                            {renderPlatformIcons(platforms)}
                                                        </small>
                                                    </div>
                                                </div>
                                                <button
                                                    className="btn btn-sm btn-primary"
                                                    onClick={() => generateFromAI(g.id)}
                                                    disabled={selectedGameId !== null}
                                                >
                                                    {selectedGameId === g.id && isLoading ? (
                                                        <>
                                                            <span className="spinner-border spinner-border-sm me-1"></span>
                                                            Loading...
                                                        </>
                                                    ) : (
                                                        'Select'
                                                    )}
                                                </button>
                                            </div>
                                        );
                                    })
                                ) : (
                                    <div className="text-center py-4">
                                        <FontAwesomeIcon icon={faRobot} size="2x" className="text-muted mb-3" />
                                        <p className="text-muted">Search for your game above</p>
                                    </div>
                                )}
                            </div>
                        )}

                        {loadError && errorGameId && (
                            <div className="alert alert-danger mt-3">
                                An error occurred while fetching game data. Please try again or use the manual form.
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                <>
                    <div className="text-center mb-4">
                        <button 
                            className="btn btn-primary btn-lg"
                            onClick={startAIWorkflow}
                        >
                            <FontAwesomeIcon icon={faRobot} className="me-2" />
                            Fill Form Automatically with AI
                        </button>
                        <div className="mt-2">
                            <small className="text-white">Or fill out the form manually below</small>
                        </div>
                    </div>

                    <GameTitleForm
                        gameTitle={title}
                        campaignData={{}}
                        externalGameData={externalGameTitle}
                        communities={communities}
                        setGameTitle={setTitle}
                        setMainImageBlob={setMainImageBlob}
                        setBannerImageBlob={setBannerImageBlob}
                        errors={errors}
                        showPressKit={false}
                        isCreate={true}
                        requireCommunity={true}
                        onMediaIdsUpdate={handleMediaIdsUpdate}
                    />

                    <div className='container'>
                        {Object.keys(errors).length > 0 &&
                            Object.keys(errors).map((errorKey) => {
                                const errorValue = errors[errorKey];

                                if (Array.isArray(errorValue)) {
                                    return errorValue.map((message, index) => (
                                        <Danger key={`${errorKey}-${index}`} message={message} />
                                    ));
                                }

                                return (
                                    <Danger key={errorKey} message={errorValue} />
                                );
                            })}

                        {errors && Object.keys(errors).length > 0 && (
                            <Danger message="You have errors in your submission." />
                        )}
                    </div>

                    <div className="mt-4 text-center">
                        <button
                            type="button"
                            onClick={handleSubmit}
                            className="btn btn-success mx-2"
                            disabled={isLoading}
                        >
                            {isLoading ? <Loading /> : 'Create Game & Continue'}
                        </button>
                    </div>
                </>
            )}

 
        </div>
    );
};

export default PublisherOnboardingStep4Page;